import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Brain, TrendingUp, RefreshCw, Lightbulb, Target, BarChart3,
  AlertTriangle, ChevronRight, Loader2, CheckCircle, Shield,
  DollarSign, Clock, Activity, ArrowUp, ArrowDown, Minus,
  History, Calendar, Zap, TrendingDown, Info, FileText, Sparkles,
  ChevronDown, ChevronUp, Factory, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// ============================================================
// Composants utilitaires
// ============================================================

function RatioField({ label, value, onChange, cible, unit = "%" }) {
  const diff = parseFloat(value || 0) - parseFloat(cible || 0);
  const isGood = Math.abs(diff) <= 1.5;
  const isBad = Math.abs(diff) > 3;
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <Label className="text-xs font-medium text-gray-600">{label}</Label>
        <span className={`text-xs font-semibold ${isGood ? 'text-green-600' : isBad ? 'text-red-600' : 'text-amber-600'}`}>
          Cible: {cible}{unit}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          step="0.1"
          value={value}
          onChange={e => onChange(e.target.value)}
          className={`h-8 text-sm ${isBad ? 'border-red-300 bg-red-50' : isGood && parseFloat(value) > 0 ? 'border-green-300 bg-green-50' : ''}`}
        />
        <span className="text-xs text-gray-400 w-4">{unit}</span>
        {parseFloat(value) > 0 && (
          <span className={`text-xs font-bold w-14 text-right ${diff > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {diff > 0 ? '+' : ''}{diff.toFixed(1)}{unit}
          </span>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status, risk }) {
  const val = status || risk;
  const map = {
    stable: { label: 'Stable', color: 'bg-green-100 text-green-800 border-green-300' },
    attention: { label: 'Attention', color: 'bg-amber-100 text-amber-800 border-amber-300' },
    warning: { label: 'Alerte', color: 'bg-red-100 text-red-800 border-red-300' },
    green: { label: 'Vert', color: 'bg-green-100 text-green-800 border-green-300' },
    yellow: { label: 'Jaune', color: 'bg-amber-100 text-amber-800 border-amber-300' },
    red: { label: 'Rouge', color: 'bg-red-100 text-red-800 border-red-300' },
  };
  const cfg = map[val] || { label: val, color: 'bg-gray-100 text-gray-700' };
  return <Badge className={`${cfg.color} border font-semibold`}>{cfg.label}</Badge>;
}

function ScoreRing({ score }) {
  const color = score >= 75 ? '#16A34A' : score >= 50 ? '#D97706' : '#DC2626';
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-20 h-20">
        <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E5E7EB" strokeWidth="3" />
          <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3"
            strokeDasharray={`${score} ${100 - score}`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xl font-bold" style={{ color }}>{score}</span>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-1">Score /100</p>
    </div>
  );
}

function SeverityIcon({ severity }) {
  if (severity === 'high') return <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />;
  if (severity === 'medium') return <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />;
  return <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />;
}

function fmt(n) { return Number(n || 0).toLocaleString('fr-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' $'; }

// ============================================================
// Module Hebdomadaire
// ============================================================
function ModuleHebdo({ cibles }) {
  const [data, setData] = useState(null);
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [lastDate, setLastDate] = useState(null);

  useEffect(() => { loadData(); loadLast(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/valorisation/ia-strategique/donnees/hebdo`);
      if (res.status === 200) { const d = res.data; setData(d); setPacket(d.packet); }
    } catch (e) { toast.error('Erreur chargement données'); }
    finally { setLoading(false); }
  };

  const loadLast = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/valorisation/ia-strategique/derniere/hebdo`);
      if (res.status === 200) { const d = res.data; setResult(d.analysis); setLastDate(d.created_at); }
    } catch (e) {
      console.error('Erreur chargement dernière analyse:', e);
    }
  };

  const updateRatio = (key, val) => setPacket(p => ({ ...p, ratios: { ...p.ratios, [key]: parseFloat(val) || 0 } }));
  const updateField = (key, val) => setPacket(p => ({ ...p, [key]: parseFloat(val) || 0 }));

  const run = async () => {
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API_URL}/api/valorisation/ia-strategique/analyser/hebdo`, {
        packet, cibles
      });
      if (res.status !== 200) { const e = res.data; throw new Error(e.detail); }
      const d = res.data;
      setResult(d.analysis); setLastDate(d.created_at);
      toast.success('Analyse hebdomadaire générée !');
    } catch (e) { toast.error(e.message || 'Erreur analyse'); }
    finally { setAnalyzing(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;
  if (!data || !packet) return <p className="text-center text-gray-400 py-8">Erreur de chargement</p>;

  const ratios = packet.ratios || {};
  const c = cibles || {};

  return (
    <div className="space-y-5">
      {/* Contexte */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-blue-800">Semaine {packet.period} — Surveillance précoce</p>
          <p className="text-xs text-blue-600">Complétez les ratios si les données QB ne sont pas encore importées. Les valeurs à 0 sont ignorées dans l'analyse.</p>
        </div>
      </div>

      {/* Équipes */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm">Structure RH actuelle</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Équipes peinture', key: 'teams_painting', icon: '🎨' },
              { label: 'Chargées de projet', key: 'cp_count', icon: '👔' },
              { label: 'Représentants', key: 'sales_count', icon: '💼' },
            ].map(({ label, key, icon }) => (
              <div key={key} className="text-center">
                <p className="text-2xl mb-1">{icon}</p>
                <Input type="number" className="h-9 text-center font-bold text-lg" value={packet[key] ?? ''}
                  onChange={e => setPacket(p => ({ ...p, [key]: parseInt(e.target.value) || 0 }))} />
                <p className="text-xs text-gray-500 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Ratios */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm">Ratios de la semaine (% du CA)</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <RatioField label="Main d'œuvre" value={ratios.labor_pct} onChange={v => updateRatio('labor_pct', v)} cible={c.main_oeuvre_pct} />
            <RatioField label="Peinture" value={ratios.paint_pct} onChange={v => updateRatio('paint_pct', v)} cible={c.peinture_pct} />
            <RatioField label="Matériaux" value={ratios.materials_pct} onChange={v => updateRatio('materials_pct', v)} cible={c.materiaux_pct} />
            <RatioField label="Marge brute" value={ratios.gross_margin_pct} onChange={v => updateRatio('gross_margin_pct', v)} cible={c.marge_brute_pct} />
            <RatioField label="Frais fixes" value={ratios.fixed_costs_pct} onChange={v => updateRatio('fixed_costs_pct', v)} cible={c.frais_fixes_pct} />
            <RatioField label="Net avant impôt" value={ratios.net_pre_tax_pct} onChange={v => updateRatio('net_pre_tax_pct', v)} cible={c.net_avant_impot_pct} />
          </div>
        </CardContent>
      </Card>

      {/* Zone stratégique */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm">Zone stratégique ({c.zone_strategique_pct}%)</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-500">Dépensé ce mois-ci ($)</Label>
              <Input type="number" className="h-9 mt-1" value={packet.strategic_zone_spent_month_to_date ?? ''}
                onChange={e => updateField('strategic_zone_spent_month_to_date', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Limite mensuelle ($)</Label>
              <Input type="number" className="h-9 mt-1" value={packet.strategic_zone_limit_month ?? ''}
                onChange={e => updateField('strategic_zone_limit_month', e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Projets */}
      {packet.projects?.length > 0 && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm">Projets actifs — Suivi heures</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {packet.projects.map((p, i) => {
                const ecart = p.hours_budgeted > 0 ? ((p.hours_actual - p.hours_budgeted) / p.hours_budgeted * 100) : 0;
                return (
                  <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                    <span className="font-medium text-gray-800 truncate max-w-[200px]">{p.nom}</span>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-semibold ${p.gross_margin_pct < 38 ? 'text-red-600' : 'text-green-700'}`}>{p.gross_margin_pct}% marge</span>
                      <span className="text-xs text-gray-500">{p.hours_actual}h / {p.hours_budgeted}h</span>
                      {ecart !== 0 && <span className={`text-xs font-bold ${ecart > 8 ? 'text-red-600' : 'text-gray-400'}`}>{ecart > 0 ? '+' : ''}{ecart.toFixed(0)}%</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bouton analyse */}
      <div className="flex items-center justify-between">
        {lastDate && <p className="text-xs text-gray-400">Dernière analyse : {new Date(lastDate).toLocaleDateString('fr-CA')} à {new Date(lastDate).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}</p>}
        <Button onClick={run} disabled={analyzing} className="bg-blue-600 hover:bg-blue-700 ml-auto" data-testid="run-hebdo-btn">
          {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
          {analyzing ? 'Analyse en cours...' : 'Lancer la surveillance'}
        </Button>
      </div>

      {/* Résultats */}
      {result && <HebdoResultats result={result} />}
    </div>
  );
}

function HebdoResultats({ result }) {
  const sevColor = { high: 'border-red-200 bg-red-50', medium: 'border-amber-200 bg-amber-50', low: 'border-blue-200 bg-blue-50' };
  return (
    <div className="space-y-4 mt-2">
      <div className="flex items-center gap-3 p-4 bg-white border-2 border-gray-200 rounded-xl">
        <Activity className="w-6 h-6 text-gray-600" />
        <div>
          <div className="flex items-center gap-2 mb-1">
            <p className="font-semibold text-gray-900">Statut de la semaine</p>
            <StatusBadge status={result.status} />
          </div>
          {result.resume_fr && <p className="text-sm text-gray-600">{result.resume_fr}</p>}
        </div>
      </div>

      {(result.alerts || []).length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-700">{result.alerts.length} alerte{result.alerts.length > 1 ? 's' : ''} détectée{result.alerts.length > 1 ? 's' : ''}</p>
          {result.alerts.map((alert, i) => (
            <div key={alert.id || alert._id || i} className={`border rounded-xl p-4 ${sevColor[alert.severity] || 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-start gap-3">
                <SeverityIcon severity={alert.severity} />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-semibold text-gray-800">{alert.description}</p>
                    <Badge className={`text-xs ${alert.severity === 'high' ? 'bg-red-100 text-red-700' : alert.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                      {alert.severity === 'high' ? 'Critique' : alert.severity === 'medium' ? 'Modéré' : 'Info'}
                    </Badge>
                  </div>
                  {alert.recommended_action && (
                    <p className="text-sm text-gray-600 flex items-start gap-1">
                      <ChevronRight className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" />
                      {alert.recommended_action}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <p className="text-sm text-green-700 font-medium">Aucune dérive détectée cette semaine. Continuez sur cette trajectoire.</p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Module Mensuel
// ============================================================
function ModuleMensuel({ cibles }) {
  const [data, setData] = useState(null);
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [lastDate, setLastDate] = useState(null);

  useEffect(() => { loadData(); loadLast(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/valorisation/ia-strategique/donnees/mensuel`);
      if (res.status === 200) { const d = res.data; setData(d); setPacket(d.packet); }
    } catch { toast.error('Erreur chargement données'); }
    finally { setLoading(false); }
  };

  const loadLast = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/valorisation/ia-strategique/derniere/mensuel`);
      if (res.status === 200) { const d = res.data; setResult(d.analysis); setLastDate(d.created_at); }
    } catch (e) {
      console.error('Erreur chargement analyse mensuelle:', e);
    }
  };

  const upRatio = (key, val) => setPacket(p => ({ ...p, ratios: { ...p.ratios, [key]: parseFloat(val) || 0 } }));
  const upZone = (key, val) => setPacket(p => ({ ...p, strategic_zone: { ...p.strategic_zone, [key]: parseFloat(val) || 0 } }));
  const upField = (key, val) => setPacket(p => ({ ...p, [key]: parseFloat(val) || 0 }));

  const run = async () => {
    // Recalculer zone disponible
    const zone = packet?.strategic_zone || {};
    const available = (zone.theoretical || 0) - (zone.spent || 0);
    const updatedPacket = { ...packet, strategic_zone: { ...zone, available_real: available } };

    setAnalyzing(true);
    try {
      const res = await axios.post(`${API_URL}/api/valorisation/ia-strategique/analyser/mensuel`, {
        packet: updatedPacket, cibles
      });
      if (res.status !== 200) { const e = res.data; throw new Error(e.detail); }
      const d = res.data;
      setResult(d.analysis); setLastDate(d.created_at);
      toast.success('Rapport mensuel généré !');
    } catch (e) { toast.error(e.message || 'Erreur analyse'); }
    finally { setAnalyzing(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-green-500" /></div>;
  if (!data || !packet) return null;

  const ratios = packet.ratios || {};
  const zone = packet.strategic_zone || {};
  const c = cibles || {};

  return (
    <div className="space-y-5">
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex gap-3">
        <Info className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-green-800">Période : {packet.period} — Rapport stratégique mensuel</p>
          <p className="text-xs text-green-600">Les cibles viennent directement de votre configuration. Vérifiez et complétez les chiffres réels si nécessaire.</p>
        </div>
      </div>

      {/* CA & Finances */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm">Chiffre d'affaires & Finances</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-gray-500">CA ce mois ($)</Label>
              <Input type="number" className="h-9 mt-1 font-bold" value={packet.ca ?? ''} onChange={e => upField('ca', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">CA mois précédent ($)</Label>
              <Input type="number" className="h-9 mt-1" value={packet.ca_mois_precedent ?? ''} onChange={e => upField('ca_mois_precedent', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Frais fixes réels ($)</Label>
              <Input type="number" className="h-9 mt-1" value={packet.frais_fixes_reel ?? ''} onChange={e => upField('frais_fixes_reel', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Cash disponible ($)</Label>
              <Input type="number" className="h-9 mt-1" value={packet.cash_disponible ?? ''} onChange={e => upField('cash_disponible', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Réserve cible 6 mois ($)</Label>
              <Input type="number" className="h-9 mt-1 bg-gray-50" value={Math.round(packet.reserve_cible_6_mois || 0)} readOnly />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Non distribué cible ($)</Label>
              <Input type="number" className="h-9 mt-1 bg-gray-50" value={Math.round(packet.non_distribue_cible || 0)} readOnly />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ratios */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm">Ratios réels vs cibles plateforme</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <RatioField label="Marge brute" value={ratios.gross_margin_pct} onChange={v => upRatio('gross_margin_pct', v)} cible={c.marge_brute_pct} />
            <RatioField label="Main d'œuvre" value={ratios.labor_pct} onChange={v => upRatio('labor_pct', v)} cible={c.main_oeuvre_pct} />
            <RatioField label="Matériaux" value={ratios.materials_pct} onChange={v => upRatio('materials_pct', v)} cible={c.materiaux_pct} />
            <RatioField label="Peinture" value={ratios.paint_pct} onChange={v => upRatio('paint_pct', v)} cible={c.peinture_pct} />
            <RatioField label="Frais fixes" value={ratios.fixed_costs_pct} onChange={v => upRatio('fixed_costs_pct', v)} cible={c.frais_fixes_pct} />
            <RatioField label="Net avant impôt" value={ratios.net_pre_tax_pct} onChange={v => upRatio('net_pre_tax_pct', v)} cible={c.net_avant_impot_pct} />
          </div>
        </CardContent>
      </Card>

      {/* Zone stratégique */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            Zone stratégique ({c.zone_strategique_pct}%)
            <Badge className="bg-purple-100 text-purple-700 text-xs">Capital libre</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-gray-500">Théorique ($)</Label>
              <Input type="number" className="h-9 mt-1 bg-gray-50" value={Math.round(zone.theoretical || 0)} readOnly />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Dépensé ($)</Label>
              <Input type="number" className="h-9 mt-1" value={zone.spent ?? ''} onChange={e => upZone('spent', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Disponible réel ($)</Label>
              <Input type="number" className="h-9 mt-1 bg-purple-50 font-bold" value={Math.round((zone.theoretical || 0) - (zone.spent || 0))} readOnly />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        {lastDate && <p className="text-xs text-gray-400">Dernière analyse : {new Date(lastDate).toLocaleDateString('fr-CA')}</p>}
        <Button onClick={run} disabled={analyzing} className="bg-green-600 hover:bg-green-700 ml-auto" data-testid="run-mensuel-btn">
          {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
          {analyzing ? 'Analyse en cours...' : 'Générer le rapport mensuel'}
        </Button>
      </div>

      {result && <MensuelResultats result={result} />}
    </div>
  );
}

function MensuelResultats({ result }) {
  return (
    <div className="space-y-4">
      {/* Score + Risque */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="border-2">
          <CardContent className="pt-4 flex flex-col items-center">
            <ScoreRing score={result.score || 0} />
            <StatusBadge risk={result.risk} />
          </CardContent>
        </Card>
        <Card className="border-2">
          <CardContent className="pt-4">
            <p className="text-xs font-semibold text-gray-500 mb-2">Résumé exécutif</p>
            <p className="text-sm text-gray-800">{result.resume_fr}</p>
          </CardContent>
        </Card>
      </div>

      {/* KPI variance */}
      {result.kpi_variance && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Écarts vs cibles</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Marge brute', val: result.kpi_variance.gross_margin_delta },
                { label: 'Frais fixes', val: -result.kpi_variance.fixed_costs_delta },
                { label: 'Net av. impôt', val: result.kpi_variance.net_pre_tax_delta },
              ].map(({ label, val }) => (
                <div key={label} className="text-center p-3 rounded-lg bg-gray-50">
                  <p className="text-xs text-gray-500">{label}</p>
                  <div className={`flex items-center justify-center gap-1 mt-1 text-lg font-bold ${val > 0 ? 'text-green-600' : val < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                    {val > 0 ? <ArrowUp className="w-4 h-4" /> : val < 0 ? <ArrowDown className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                    {val > 0 ? '+' : ''}{(val || 0).toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Zone stratégique */}
      {result.strategic_zone && (
        <Card className="border-purple-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-purple-700">Zone stratégique</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><p className="text-xs text-gray-500">Théorique</p><p className="font-bold text-gray-800">{fmt(result.strategic_zone.theoretical)}</p></div>
              <div><p className="text-xs text-gray-500">Dépensé</p><p className="font-bold text-amber-600">{fmt(result.strategic_zone.spent)}</p></div>
              <div><p className="text-xs text-gray-500">Disponible</p><p className="font-bold text-purple-700">{fmt(result.strategic_zone.available_real)}</p></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Drivers */}
      {result.top_drivers?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Drivers principaux</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {result.top_drivers.map((d, i) => (
              <div key={d.id || d._id || i} className="flex justify-between items-start p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700 flex-1">{d.cause}</p>
                {d.impact_estimated !== 0 && (
                  <span className={`text-sm font-bold ml-3 ${d.impact_estimated > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {d.impact_estimated > 0 ? '+' : ''}{fmt(d.impact_estimated)}
                  </span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Recommandations */}
      {result.recommendations?.length > 0 && (
        <Card className="border-blue-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-blue-700">Recommandations chiffrées</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {result.recommendations.map((r, i) => (
              <div key={r.id || r._id || i} className="flex gap-3 p-3 bg-blue-50 rounded-lg">
                <span className="w-6 h-6 bg-blue-600 text-white rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0">{r.priority || i + 1}</span>
                <div className="flex-1">
                  <p className="text-sm text-gray-800">{r.action}</p>
                  {r.expected_profit_impact !== 0 && (
                    <p className="text-xs font-semibold text-blue-700 mt-1">Impact estimé : {fmt(r.expected_profit_impact)}</p>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Plan 30 jours */}
      {result.plan_30_jours?.length > 0 && (
        <Card className="border-green-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-green-700">Plan 30 jours</CardTitle></CardHeader>
          <CardContent>
            <ol className="space-y-2">
              {result.plan_30_jours.map((step, i) => (
                <li key={step.id || step._id || i} className="flex gap-2 text-sm text-gray-700">
                  <span className="font-bold text-green-600 w-5 flex-shrink-0">{i + 1}.</span>
                  {step}
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================
// Module Trimestriel
// ============================================================
function ModuleTrimestriel({ cibles }) {
  const [data, setData] = useState(null);
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [lastDate, setLastDate] = useState(null);

  useEffect(() => { loadData(); loadLast(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/valorisation/ia-strategique/donnees/trimestriel`);
      if (res.status === 200) { const d = res.data; setData(d); setPacket(d.packet); }
    } catch { toast.error('Erreur chargement données'); }
    finally { setLoading(false); }
  };

  const loadLast = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/valorisation/ia-strategique/derniere/trimestriel`);
      if (res.status === 200) { const d = res.data; setResult(d.analysis); setLastDate(d.created_at); }
    } catch (e) {
      console.error('Erreur chargement analyse trimestrielle:', e);
    }
  };

  const upRoi = (key, val) => setPacket(p => ({ ...p, roi_scenarios: { ...p.roi_scenarios, [key]: parseFloat(val) || 0 } }));
  const upField = (key, val) => setPacket(p => ({ ...p, [key]: parseFloat(val) || 0 }));

  const run = async () => {
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API_URL}/api/valorisation/ia-strategique/analyser/trimestriel`, {
        packet, cibles
      });
      if (res.status !== 200) { const e = res.data; throw new Error(e.detail); }
      const d = res.data;
      setResult(d.analysis); setLastDate(d.created_at);
      toast.success('Arbitrage trimestriel généré !');
    } catch (e) { toast.error(e.message || 'Erreur analyse'); }
    finally { setAnalyzing(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-purple-500" /></div>;
  if (!data || !packet) return null;

  const roi = packet.roi_scenarios || {};
  const c = cibles || {};

  return (
    <div className="space-y-5">
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex gap-3">
        <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-purple-800">Période : {packet.period} — Arbitrage capital</p>
          <p className="text-xs text-purple-600">Vision 5 ans : CA 3,2M–3,6M$, 800K+ avant impôt, 6 équipes, trajectoire 10M+. Discipline de marge non négociable.</p>
        </div>
      </div>

      {/* Trimestre en chiffres */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm">Résultats du trimestre</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-gray-500">CA trimestriel ($)</Label>
              <Input type="number" className="h-9 mt-1 font-bold" value={packet.ca_trimestriel ?? ''} onChange={e => upField('ca_trimestriel', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Marge brute réelle (%)</Label>
              <Input type="number" step="0.1" className="h-9 mt-1" value={packet.marge_brute_trim_pct ?? ''} onChange={e => upField('marge_brute_trim_pct', e.target.value)} />
              {(packet.marge_brute_trim_pct || 0) >= 99 && (
                <p className="text-xs text-amber-600 mt-0.5">Vérifiez ce chiffre (données QB pas encore importées ?)</p>
              )}
            </div>
            <div>
              <Label className="text-xs text-gray-500">Capital (placements) ($)</Label>
              <Input type="number" className="h-9 mt-1" value={packet.capital_placements ?? ''} onChange={e => upField('capital_placements', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Capital disponible estimé ($)</Label>
              <Input type="number" className="h-9 mt-1 bg-purple-50 font-bold" value={Math.round(packet.capital_disponible_estime || 0)} onChange={e => upField('capital_disponible_estime', e.target.value)} />
            </div>
          </div>

          {/* Protections obligatoires */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs font-semibold text-gray-600 mb-2">Protections obligatoires (à déduire avant arbitrage)</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500">Réserve min. 6 mois ($)</Label>
                <Input type="number" className="h-8 mt-1 bg-white" value={Math.round(packet.reserve_min_requise || 0)} onChange={e => upField('reserve_min_requise', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Non distribué 10% ($)</Label>
                <Input type="number" className="h-8 mt-1 bg-white" value={Math.round(packet.non_distribue_min || 0)} onChange={e => upField('non_distribue_min', e.target.value)} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scénarios ROI */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Scénarios d'allocation — Impact 12 mois estimé ($)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-500">Ajout d'une équipe ($)</Label>
              <Input type="number" className="h-9 mt-1" value={roi.ajout_equipe_12m ?? ''} onChange={e => upRoi('ajout_equipe_12m', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Placement externe 8% ($)</Label>
              <Input type="number" className="h-9 mt-1 bg-gray-50" value={Math.round(roi.placement_externe_8pct || 0)} readOnly />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Marketing / acquisition ($)</Label>
              <Input type="number" className="h-9 mt-1" value={roi.marketing_estime ?? ''} onChange={e => upRoi('marketing_estime', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Division complémentaire ($)</Label>
              <Input type="number" className="h-9 mt-1" value={roi.division_complementaire ?? ''} onChange={e => upRoi('division_complementaire', e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2">Note: L'IA ajustera ces estimations selon la discipline de marge requise.</p>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        {lastDate && <p className="text-xs text-gray-400">Dernière analyse : {new Date(lastDate).toLocaleDateString('fr-CA')}</p>}
        <Button onClick={run} disabled={analyzing} className="bg-purple-600 hover:bg-purple-700 ml-auto" data-testid="run-trimestriel-btn">
          {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Target className="w-4 h-4 mr-2" />}
          {analyzing ? 'Arbitrage en cours...' : 'Lancer l\'arbitrage capital'}
        </Button>
      </div>

      {result && <TrimestrielResultats result={result} />}
    </div>
  );
}

function TrimestrielResultats({ result }) {
  const strategyLabels = {
    add_team: 'Ajouter une équipe',
    scale_marketing: 'Accélérer le marketing',
    expand_division: 'Expansion division',
    invest_external: 'Investissement externe',
    hold: 'Maintenir / stabiliser',
  };
  const riskColor = { low: 'text-green-600', moderate: 'text-amber-600', high: 'text-red-600' };
  const riskLabel = { low: 'Faible', moderate: 'Modéré', high: 'Élevé' };

  return (
    <div className="space-y-4">
      {/* Capital disponible + résumé */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="border-purple-300 bg-purple-50">
          <CardContent className="pt-4">
            <p className="text-xs text-purple-600 font-semibold">Capital libre réel disponible</p>
            <p className="text-3xl font-bold text-purple-800 mt-1">{fmt(result.capital_available_real)}</p>
          </CardContent>
        </Card>
        <Card className="border-2">
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 font-semibold mb-2">Résumé</p>
            <p className="text-sm text-gray-800">{result.resume_fr}</p>
          </CardContent>
        </Card>
      </div>

      {/* Décision principale */}
      {result.decision_primary && (
        <Card className="border-green-300 bg-green-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-green-800 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Décision recommandée
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-3">
              <Badge className="bg-green-200 text-green-800 font-bold text-sm whitespace-nowrap">
                {strategyLabels[result.decision_primary.strategy] || result.decision_primary.strategy}
              </Badge>
              <div>
                <p className="text-sm text-gray-800">{result.decision_primary.justification}</p>
                {result.decision_primary.expected_12_month_profit_impact > 0 && (
                  <p className="text-sm font-bold text-green-700 mt-2">Impact 12 mois : +{fmt(result.decision_primary.expected_12_month_profit_impact)}</p>
                )}
                {result.decision_primary.expected_5_year_impact && (
                  <p className="text-xs text-gray-500 mt-1">Vision 5 ans : {result.decision_primary.expected_5_year_impact}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Options alternatives */}
      {result.secondary_options_ranked?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Alternatives classées</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {result.secondary_options_ranked.map((opt, i) => (
              <div key={opt.id || opt._id || i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 bg-gray-200 text-gray-700 rounded-full text-xs font-bold flex items-center justify-center">{opt.rank}</span>
                  <p className="text-sm text-gray-700">{opt.strategy}</p>
                </div>
                {opt.roi_estime > 0 && <span className="text-sm font-bold text-gray-600">{fmt(opt.roi_estime)}</span>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Risque structurel */}
      {result.risk_assessment && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Évaluation du risque structurel</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3 mb-3">
              {[
                { label: 'Risque marge', val: result.risk_assessment.margin_risk },
                { label: 'Risque cash', val: result.risk_assessment.cash_risk },
                { label: 'Risque structure', val: result.risk_assessment.structure_risk },
              ].map(({ label, val }) => (
                <div key={label} className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className={`font-bold mt-1 ${riskColor[val] || 'text-gray-600'}`}>{riskLabel[val] || val}</p>
                </div>
              ))}
            </div>
            {result.risk_assessment.commentaire && (
              <p className="text-sm text-gray-600 italic">{result.risk_assessment.commentaire}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Alignement vision 5 ans */}
      {result.alignment_5_ans && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-blue-700">Alignement vision 5 ans</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-blue-800">{result.alignment_5_ans}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================
// Historique
// ============================================================
function Historique() {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/valorisation/ia-strategique/historique?limit=30`)
      .then(r => r.status === 200 ? r.data : { analyses: [] })
      .then(d => setAnalyses(d.analyses || []))
      .finally(() => setLoading(false));
  }, []);

  const moduleLabels = { hebdo: 'Hebdomadaire', mensuel: 'Mensuel', trimestriel: 'Trimestriel' };
  const moduleColors = { hebdo: 'bg-blue-100 text-blue-700', mensuel: 'bg-green-100 text-green-700', trimestriel: 'bg-purple-100 text-purple-700' };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-3">
      {analyses.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>Aucune analyse enregistrée</p>
        </div>
      ) : analyses.map((a) => {
        const score = a.analysis?.score;
        const status = a.analysis?.status || a.analysis?.risk;
        return (
          <div key={a.id} className="border rounded-xl p-4 flex items-center gap-4 hover:shadow-sm transition-shadow">
            <Badge className={`${moduleColors[a.module] || 'bg-gray-100 text-gray-700'} font-semibold whitespace-nowrap`}>
              {moduleLabels[a.module] || a.module}
            </Badge>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800">{a.period || '—'}</p>
              {a.analysis?.resume_fr && <p className="text-xs text-gray-500 truncate">{a.analysis.resume_fr}</p>}
            </div>
            <div className="flex items-center gap-3">
              {score !== undefined && <div className="text-center"><p className="text-lg font-bold text-gray-800">{score}</p><p className="text-xs text-gray-400">/100</p></div>}
              {status && <StatusBadge status={status} risk={status} />}
              <p className="text-xs text-gray-400 whitespace-nowrap">{new Date(a.created_at).toLocaleDateString('fr-CA')}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// Module Rapports IA Complets
// ============================================================

function StatutBadge({ statut }) {
  const map = {
    vert: 'bg-green-100 text-green-700 border-green-200',
    jaune: 'bg-amber-100 text-amber-700 border-amber-200',
    rouge: 'bg-red-100 text-red-700 border-red-200',
  };
  const labels = { vert: 'Bon', jaune: 'Attention', rouge: 'Risque' };
  return (
    <Badge className={`${map[statut] || 'bg-gray-100 text-gray-600'} border text-xs font-semibold`}>
      {labels[statut] || statut}
    </Badge>
  );
}

function SectionRapport({ section, icon: Icon, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!section) return null;
  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setOpen(o => !o)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Icon className="w-4 h-4 text-slate-600" />
            {section.titre_section}
          </CardTitle>
          <div className="flex items-center gap-2">
            <StatutBadge statut={section.statut} />
            {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="pt-0">
          <p className="text-sm text-gray-700 mb-3 leading-relaxed">{section.texte}</p>
          {section.kpis?.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {section.kpis.map((kpi, i) => (
                <div key={kpi.id || kpi._id || i} className="p-2 bg-gray-50 rounded-lg text-center">
                  <p className="text-xs text-gray-500">{kpi.label}</p>
                  <p className="text-sm font-bold text-gray-800 mt-0.5">{kpi.valeur}</p>
                  {kpi.tendance && (
                    <p className={`text-xs mt-0.5 ${kpi.tendance === 'hausse' ? 'text-green-600' : kpi.tendance === 'baisse' ? 'text-red-600' : 'text-gray-400'}`}>
                      {kpi.tendance === 'hausse' ? '↑' : kpi.tendance === 'baisse' ? '↓' : '→'}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function RapportViewer({ rapport, periode, created_at }) {
  if (!rapport) return null;
  const sevColor = { critique: 'border-red-200 bg-red-50', modere: 'border-amber-200 bg-amber-50', info: 'border-blue-200 bg-blue-50' };
  const scoreColor = (rapport.score_global || 0) >= 75 ? '#16A34A' : (rapport.score_global || 0) >= 50 ? '#D97706' : '#DC2626';

  return (
    <div className="space-y-4">
      {/* En-tête rapport */}
      <div className="flex items-start justify-between gap-4 p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl border border-slate-200">
        <div className="flex-1">
          <h3 className="font-bold text-gray-900 text-lg">{rapport.titre}</h3>
          <p className="text-xs text-gray-500 mt-0.5">Généré le {new Date(created_at).toLocaleDateString('fr-CA')} à {new Date(created_at).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}</p>
          <p className="text-sm text-gray-700 mt-2 leading-relaxed">{rapport.resume_executif}</p>
        </div>
        {rapport.score_global > 0 && (
          <div className="text-center shrink-0">
            <div className="relative w-16 h-16">
              <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E5E7EB" strokeWidth="3" />
                <circle cx="18" cy="18" r="15.9" fill="none" stroke={scoreColor} strokeWidth="3"
                  strokeDasharray={`${rapport.score_global} ${100 - rapport.score_global}`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-bold" style={{ color: scoreColor }}>{rapport.score_global}</span>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">/100</p>
          </div>
        )}
      </div>

      {/* Sections analytiques */}
      <div className="space-y-3">
        <SectionRapport section={rapport.sections?.ventes} icon={TrendingUp} defaultOpen={true} />
        <SectionRapport section={rapport.sections?.production} icon={Factory} />
        <SectionRapport section={rapport.sections?.finances} icon={DollarSign} />
      </div>

      {/* Scénarios */}
      {rapport.scenarios_ventes && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Target className="w-4 h-4 text-blue-600" /> Scénarios de projection des ventes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { key: 'pessimiste', label: 'Pessimiste -20%', color: 'border-red-200 bg-red-50 text-red-700' },
                { key: 'realiste', label: 'Réaliste', color: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
                { key: 'optimiste', label: 'Optimiste +20%', color: 'border-blue-200 bg-blue-50 text-blue-700' },
              ].map(s => {
                const sc = rapport.scenarios_ventes[s.key];
                if (!sc) return null;
                return (
                  <div key={s.key} className={`p-3 rounded-lg border ${s.color}`}>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-1">{s.label}</p>
                    {sc.projection_annuelle > 0 && <p className="text-lg font-bold">{sc.projection_annuelle.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 })}</p>}
                    <p className="text-xs mt-1 opacity-80">{sc.description}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alertes */}
      {rapport.alertes?.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-700">{rapport.alertes.length} alerte{rapport.alertes.length > 1 ? 's' : ''}</p>
          {rapport.alertes.map((a, i) => (
            <div key={a.id || a._id || i} className={`border rounded-xl p-3 ${sevColor[a.severite] || 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${a.severite === 'critique' ? 'text-red-500' : a.severite === 'modere' ? 'text-amber-500' : 'text-blue-500'}`} />
                <div>
                  <p className="text-sm font-semibold text-gray-800">{a.description}</p>
                  {a.action_immediate && <p className="text-xs text-gray-600 mt-1 flex items-start gap-1"><ChevronRight className="w-3 h-3 shrink-0 mt-0.5" />{a.action_immediate}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recommandations */}
      {rapport.recommandations?.length > 0 && (
        <Card className="border-green-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-green-700 flex items-center gap-2"><Lightbulb className="w-4 h-4" /> Recommandations</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {rapport.recommandations.map((r, i) => (
              <div key={r.id || r._id || i} className="flex gap-3 p-3 bg-green-50 rounded-lg">
                <span className="w-6 h-6 bg-green-600 text-white rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0">{r.priorite || i + 1}</span>
                <div>
                  <p className="text-sm text-gray-800">{r.action}</p>
                  {r.impact_estime > 0 && <p className="text-xs font-semibold text-green-700 mt-0.5">Impact estimé : {r.impact_estime.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 })}</p>}
                  {r.echeance && <p className="text-xs text-gray-400 mt-0.5">Échéance : {r.echeance}</p>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Plan 30 jours */}
      {rapport.plan_30_jours?.length > 0 && (
        <Card className="border-purple-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-purple-700 flex items-center gap-2"><Zap className="w-4 h-4" /> Plan 30 jours</CardTitle></CardHeader>
          <CardContent>
            <ol className="space-y-2">
              {rapport.plan_30_jours.map((step, i) => (
                <li key={step.id || step._id || i} className="flex gap-2 text-sm text-gray-700">
                  <span className="font-bold text-purple-600 w-5 flex-shrink-0">{i + 1}.</span>{step}
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}

      {/* Réussites & Axes améliorations (annuel) */}
      {rapport.reussites?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="border-emerald-200 bg-emerald-50/40">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-emerald-700 flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Réussites de l'année</CardTitle></CardHeader>
            <CardContent><ul className="space-y-1">{rapport.reussites.map((r, i) => <li key={i} className="text-sm text-gray-700 flex gap-2"><span className="text-emerald-500">✓</span>{r}</li>)}</ul></CardContent>
          </Card>
          {rapport.axes_amelioration?.length > 0 && (
            <Card className="border-amber-200 bg-amber-50/40">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-700 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Axes d'amélioration</CardTitle></CardHeader>
              <CardContent><ul className="space-y-1">{rapport.axes_amelioration.map((a, i) => <li key={i} className="text-sm text-gray-700 flex gap-2"><span className="text-amber-500">→</span>{a}</li>)}</ul></CardContent>
            </Card>
          )}
        </div>
      )}

      {/* TOTAL DU RAPPORT — Résumé final */}
      <Card className="border-2 border-[#1a237e] bg-[#1a237e]/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-bold text-[#1a237e] flex items-center gap-2">
            <BarChart3 className="w-5 h-5" /> Résumé Total du Rapport
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            {['ventes', 'production', 'finances'].map(key => {
              const section = rapport.sections?.[key];
              if (!section) return null;
              const statusColor = section.statut === 'vert' ? 'bg-green-100 border-green-300 text-green-800' : section.statut === 'jaune' ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-red-100 border-red-300 text-red-800';
              return (
                <div key={key} className={`p-4 rounded-xl border-2 ${statusColor}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide mb-1">{section.titre_section}</p>
                  <StatutBadge statut={section.statut} />
                  {section.kpis?.slice(0, 2).map((kpi, i) => (
                    <p key={kpi.id || kpi._id || i} className="text-xs mt-1.5"><span className="font-medium">{kpi.label}:</span> {kpi.valeur}</p>
                  ))}
                </div>
              );
            })}
          </div>
          {rapport.score_global > 0 && (
            <div className="text-center py-2 border-t border-[#1a237e]/20">
              <p className="text-sm text-gray-600">Score global de santé</p>
              <p className="text-3xl font-black text-[#1a237e]">{rapport.score_global}<span className="text-lg font-normal text-gray-400">/100</span></p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ModuleRapports() {
  const now = new Date();
  const [typeRapport, setTypeRapport] = useState('mensuel');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [generating, setGenerating] = useState(false);
  const [rapports, setRapports] = useState([]);
  const [loadingListe, setLoadingListe] = useState(true);
  const [selectedRapport, setSelectedRapport] = useState(null);

  const MOIS_NOMS = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  const loadListe = useCallback(async () => {
    setLoadingListe(true);
    try {
      const r = await axios.get(`${API_URL}/api/valorisation/rapports/liste?limit=20`);
      const d = r.data;
      setRapports(d.rapports || []);
    } catch (e) { console.error(e); }
    setLoadingListe(false);
  }, []);

  useEffect(() => { loadListe(); }, [loadListe]);

  const getPeriode = () => {
    if (typeRapport === 'mensuel') return `${year}-${String(month).padStart(2, '0')}`;
    if (typeRapport === 'annuel') return String(year);
    return String(year);
  };

  const generer = async () => {
    setGenerating(true);
    const periode = getPeriode();
    try {
      const r = await axios.post(`${API_URL}/api/valorisation/rapports/generer/${typeRapport}/${year}/${periode}`);
      if (r.status !== 200) { const e = r.data; throw new Error(e.detail); }
      const d = r.data;
      setSelectedRapport(d);
      await loadListe();
      toast.success('Rapport généré avec succès !');
    } catch (e) { toast.error(e.message || 'Erreur génération rapport'); }
    finally { setGenerating(false); }
  };

  const viewRapport = async (id) => {
    try {
      const r = await axios.get(`${API_URL}/api/valorisation/rapports/${id}`);
      if (r.status === 200) setSelectedRapport(r.data);
    } catch (e) {
      console.error('Erreur chargement rapport:', e);
    }
  };

  const typeColors = {
    mensuel: 'bg-blue-100 text-blue-700 border-blue-200',
    trimestriel: 'bg-purple-100 text-purple-700 border-purple-200',
    annuel: 'bg-amber-100 text-amber-700 border-amber-200',
  };
  const typeLabels = { mensuel: 'Mensuel', trimestriel: 'Trimestriel', annuel: 'Annuel' };

  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200 rounded-xl p-4 flex gap-3">
        <BookOpen className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-slate-800">Centre de rapports IA</p>
          <p className="text-xs text-slate-600">Générez et consultez tous vos rapports (mensuel, annuel) enrichis par l'IA. Chaque rapport compile automatiquement les données de ventes, production et finances, puis l'IA rédige une analyse narrative complète avec recommandations et scénarios.</p>
        </div>
      </div>

      {/* Générateur */}
      <Card className="border-blue-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-blue-600" /> Générer un nouveau rapport
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Type de rapport</Label>
              <div className="flex gap-2">
                {[
                  { val: 'mensuel', label: 'Mensuel', icon: Calendar },
                  { val: 'annuel', label: 'Annuel', icon: BarChart3 },
                ].map(t => (
                  <button key={t.val} onClick={() => setTypeRapport(t.val)}
                    data-testid={`rapport-type-${t.val}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${typeRapport === t.val ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-300'}`}>
                    <t.icon className="w-3.5 h-3.5" />{t.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Année</Label>
              <Input type="number" value={year} onChange={e => setYear(parseInt(e.target.value))} className="w-24 h-9" />
            </div>
            {typeRapport === 'mensuel' && (
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Mois</Label>
                <select value={month} onChange={e => setMonth(parseInt(e.target.value))}
                  className="h-9 rounded-md border border-gray-300 px-2 text-sm bg-white">
                  {MOIS_NOMS.slice(1).map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
            )}
            <Button onClick={generer} disabled={generating} className="bg-blue-600 hover:bg-blue-700 h-9" data-testid="generer-rapport-btn">
              {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération en cours...</> : <><Sparkles className="w-4 h-4 mr-2" />Générer le rapport</>}
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-2">La génération prend 15-30 secondes. L'IA compile toutes les données de la plateforme et rédige l'analyse.</p>
        </CardContent>
      </Card>

      {/* Vue du rapport sélectionné */}
      {selectedRapport?.rapport && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" /> Rapport affiché
            </h3>
            <Button variant="outline" size="sm" onClick={() => setSelectedRapport(null)}>Fermer</Button>
          </div>
          <RapportViewer rapport={selectedRapport.rapport} periode={selectedRapport.periode} created_at={selectedRapport.created_at} />
        </div>
      )}

      {/* Liste des rapports sauvegardés */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <History className="w-4 h-4 text-gray-500" /> Rapports sauvegardés
          </h3>
          <Button variant="ghost" size="sm" onClick={loadListe}><RefreshCw className="w-3.5 h-3.5 mr-1" />Actualiser</Button>
        </div>
        {loadingListe ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : rapports.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
            <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm text-gray-500 font-medium">Aucun rapport généré</p>
            <p className="text-xs text-gray-400 mt-1">Cliquez sur "Générer un nouveau rapport" pour créer votre premier rapport IA.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {rapports.map(r => (
              <div key={r.id} onClick={() => viewRapport(r.id)}
                className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer hover:shadow-sm transition-all ${selectedRapport?.id === r.id ? 'border-blue-300 bg-blue-50' : 'hover:border-gray-300 bg-white'}`}>
                <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${typeColors[r.type_rapport] || 'bg-gray-100 text-gray-700'} border text-xs font-semibold`}>
                      {typeLabels[r.type_rapport] || r.type_rapport}
                    </Badge>
                    <p className="text-sm font-semibold text-gray-800 truncate">{r.rapport?.titre || r.periode}</p>
                  </div>
                  {r.rapport?.resume_executif && (
                    <p className="text-xs text-gray-500 truncate mt-0.5">{r.rapport.resume_executif}</p>
                  )}
                </div>
                <div className="text-right shrink-0 flex items-center gap-3">
                  {r.rapport?.score_global > 0 && (
                    <div className="text-center hidden sm:block">
                      <p className="text-lg font-bold text-gray-700">{r.rapport.score_global}</p>
                      <p className="text-xs text-gray-400">/100</p>
                    </div>
                  )}
                  {r.rapport?.statut_global && <StatutBadge statut={r.rapport.statut_global} />}
                  <p className="text-xs text-gray-400 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString('fr-CA')}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Page principale
// ============================================================
export function CroissanceIA() {
  const [cibles, setCibles] = useState(null);
  const [activeTab, setActiveTab] = useState('hebdo');

  useEffect(() => {
    axios.get(`${API_URL}/api/configuration/objectifs-marge`)
      .then(r => r.status === 200 ? r.data : {})
      .then(d => setCibles({
        main_oeuvre_pct: d.objectif_employes_pct || 45,
        peinture_pct: d.objectif_peinture_pct || 9,
        materiaux_pct: d.objectif_materiaux_pct || 3,
        marge_brute_pct: d.objectif_marge_brute_ideal || 43,
        frais_fixes_pct: d.objectif_frais_fixes_pct || 15,
        net_avant_impot_pct: d.objectif_net_pct || 18,
        zone_strategique_pct: 8,
        reserve_mois: 6,
        non_distribue_pct: 10,
      }));
  }, []);

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Brain className="w-7 h-7 text-purple-600" />
          Moteur IA Stratégique PRQC
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Pilotage 2026–2031 — Croissance agressive, discipline de marge non négociable, trajectoire 10M+
        </p>
        {cibles && (
          <div className="flex flex-wrap gap-2 mt-2">
            <Badge className="bg-green-100 text-green-700 text-xs">Marge cible: {cibles.marge_brute_pct}%</Badge>
            <Badge className="bg-blue-100 text-blue-700 text-xs">Net cible: {cibles.net_avant_impot_pct}%</Badge>
            <Badge className="bg-amber-100 text-amber-700 text-xs">MO cible: {cibles.main_oeuvre_pct}%</Badge>
            <Badge className="bg-purple-100 text-purple-700 text-xs">Zone strat.: {cibles.zone_strategique_pct}%</Badge>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="hebdo" className="flex items-center gap-1.5" data-testid="tab-hebdo">
            <Activity className="w-4 h-4" />
            <span className="hidden sm:inline">Surveillance</span>
            <span className="sm:hidden">Hebdo</span>
          </TabsTrigger>
          <TabsTrigger value="mensuel" className="flex items-center gap-1.5" data-testid="tab-mensuel">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Rapport mensuel</span>
            <span className="sm:hidden">Mensuel</span>
          </TabsTrigger>
          <TabsTrigger value="trimestriel" className="flex items-center gap-1.5" data-testid="tab-trimestriel">
            <Target className="w-4 h-4" />
            <span className="hidden sm:inline">Arbitrage capital</span>
            <span className="sm:hidden">Trimestriel</span>
          </TabsTrigger>
          <TabsTrigger value="rapports" className="flex items-center gap-1.5" data-testid="tab-rapports">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Rapports IA</span>
            <span className="sm:hidden">Rapports</span>
          </TabsTrigger>
          <TabsTrigger value="historique" className="flex items-center gap-1.5" data-testid="tab-historique">
            <History className="w-4 h-4" />
            Historique
          </TabsTrigger>
        </TabsList>

        <TabsContent value="hebdo" className="mt-6">
          {cibles && <ModuleHebdo cibles={cibles} />}
        </TabsContent>
        <TabsContent value="mensuel" className="mt-6">
          {cibles && <ModuleMensuel cibles={cibles} />}
        </TabsContent>
        <TabsContent value="trimestriel" className="mt-6">
          {cibles && <ModuleTrimestriel cibles={cibles} />}
        </TabsContent>
        <TabsContent value="rapports" className="mt-6">
          <ModuleRapports />
        </TabsContent>
        <TabsContent value="historique" className="mt-6">
          <Historique />
        </TabsContent>
      </Tabs>
    </div>
  );
}
